import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils'
import {getResponsiveImageAttributes} from '@salesforce/retail-react-app/app/utils/responsive-image'

const stripQueryParams = (url) => (url && typeof url === 'string' ? url.split('?')[0] : null)

/** Must match PLP product-tile DynamicImage widths. */
const PLP_TILE_IMAGE_WIDTHS = ['50vw', '50vw', '25vw', '25vw', '25vw']
// Derive the Nth image URL (1-based) from the first image by inserting the
// `_alt{N-1}` token before the `_WxH.ext` size suffix (e.g. `_07I_1920x2880.jpg`
// -> `_07I_alt2_1920x2880.jpg`). Lets the small grid paint the Nth image at first
// paint, before Phase-2 enrichment loads the full imageGroups. Returns null when
// the URL doesn't match the expected pattern so callers can fall back.
const deriveNthImageUrl = (url, imageNumber) => {
    if (!url || typeof url !== 'string' || imageNumber <= 1) return null
    const [base, query] = url.split('?')
    if (!/_\d+x\d+\.\w+$/i.test(base)) return null
    const derived = base.replace(/(_\d+x\d+\.\w+)$/i, `_alt${imageNumber - 1}$1`)
    return query ? `${derived}?${query}` : derived
}

const buildImageUrl = (url, enableResizeParams) => {
    if (!url) return ''
    const baseUrl = stripQueryParams(url)
    return enableResizeParams ? `${baseUrl}[?sw={width}&q=60]` : baseUrl
}

/**
 * Builds preload attributes for the first (top-left) PLP product tile image.
 * Uses the same srcset/sizes as DynamicImage so the browser picks the LCP candidate.
 * @param {Object} product - Product search hit (Phase 1 root `image` is enough)
 * @param {boolean} enableResizeParams - Whether PRODUCT_IMGS_RESIZE_PARAMS_ENABLED is on
 * @returns {{href: string, imagesrcset?: string, imagesizes?: string}|null}
 */
const getPlpLcpImagePreloadAttrs = (product, enableResizeParams) => {
    const rawUrl = product?.image?.disBaseLink || product?.image?.link
    if (!rawUrl) return null

    const dynamicSrc = buildImageUrl(rawUrl, enableResizeParams)
    if (!enableResizeParams) {
        return {href: dynamicSrc}
    }

    const {src, srcSet, sizes} = getResponsiveImageAttributes({
        src: dynamicSrc,
        widths: PLP_TILE_IMAGE_WIDTHS
    })

    return {
        href: src,
        imagesrcset: srcSet,
        imagesizes: sizes
    }
}

const getProductTileImages = ({
    product,
    imageGroups,
    selectableAttributeId,
    initialVariationValue,
    imageViewType,
    colorValue,
    productId
}) => {
    // CASE 1: PLP without allImages - only root `image` field is available (no imageGroups)
    // This is the most performant option for PLP
    if (!imageGroups?.length && product?.image) {
        // Return the root image directly - no second image for hover
        return [
            {
                disBaseLink: product.image.disBaseLink,
                link: product.image.link,
                alt: product.image.alt,
                title: product.image.title
            },
            {} // No hover image available without allImages
        ]
    }

    // NOTE: If the selectable variation attribute doesn't exist in the products variation attributes
    // array, lets not filter the image groups on it. This ensures we always return an image for non-variant
    // type products.
    const hasSelectableAttribute = product?.variationAttributes?.find(
        ({id}) => id === selectableAttributeId
    )

    // Check if imageGroups have variationAttributes (PLP with allImages=true do, Vicky carousel products don't)
    const imageGroupsHaveVariationAttributes = imageGroups?.some(
        (group) => group.variationAttributes?.length > 0
    )

    // CASE 2: Vicky carousel products - imageGroups exist but WITHOUT variationAttributes
    // Just filter by viewType and return the first match
    if (imageGroups?.length > 0 && !imageGroupsHaveVariationAttributes) {
        const filteredImageGroups = filterImageGroups(imageGroups, {
            viewType: imageViewType,
            variationValues: {}
        })
        const matchedImageGroup = filteredImageGroups?.[0]
        return matchedImageGroup?.images?.map((img) => ({...img})) || []
    }

    // CASE 3: PLP with allImages=true - imageGroups have variationAttributes for color matching
    // For variant products (e.g., in Wishlist), use the specific color from variationValues
    const isVariant = product?.type?.variant
    const variantColor = isVariant ? product?.variationValues?.color : null

    // Extract color from root image URL (e.g., "100092730_04N_1920x2880.jpg" -> "04N")
    // This is the API's default color selection and should be used as the primary source of truth
    let colorFromRootImage = null
    if (product?.image?.disBaseLink || product?.image?.link) {
        const imageUrl = product.image.disBaseLink || product.image.link
        // Match pattern: productId_colorCode_dimensions.jpg
        const match = imageUrl.match(/\/(\d+)_([A-Z0-9]+)(?:_alt\d+)?_\d+x\d+\.jpg/i)
        if (match && match[2]) {
            colorFromRootImage = match[2]
        }
    }

    // Fallback: extract product id from relative url when available
    // Example: "/g/gift-cards/10009273004N.html" -> "10009273004N"
    const urlProductIdMatch = product?.c_relativeUrl?.match(/\/([^/]+)\.html(?:[?#].*)?$/i)
    const productIdFromRelativeUrl = urlProductIdMatch?.[1] || null

    // Extract color code from product identifier
    // Example: productId "10009273004N" -> color code "04N"
    let colorFromProductId = null
    if (!variantColor && !colorValue) {
        const currentId = productId || productIdFromRelativeUrl || product?.id
        if (currentId) {
            const colorMatch = imageGroups
                ?.filter((g) => g.viewType === 'large')
                ?.map((g) => g.variationAttributes?.[0]?.values?.[0]?.value)
                ?.find((color) => currentId.toUpperCase().endsWith(color?.toUpperCase()))
            colorFromProductId = colorMatch || null
        }
    }

    // Determine which color to use for filtering (priority order):
    // 1. Variant product with specific color (variationValues.color)
    // 2. User-selected color (colorValue state)
    // 3. Color from root image field (API's default selection)
    // 4. Color extracted from productId (for variation groups)
    // 5. Custom property c_color
    const effectiveColorValue =
        variantColor || colorValue || colorFromProductId || colorFromRootImage || product?.c_color

    const variationValues = effectiveColorValue
        ? {[selectableAttributeId]: effectiveColorValue}
        : {[selectableAttributeId]: initialVariationValue}

    const filteredImageGroups = filterImageGroups(imageGroups, {
        viewType: imageViewType,
        variationValues: hasSelectableAttribute ? variationValues : {}
    })

    // If color filtering produced no groups, fall back to viewType-only groups.
    // This prevents tiles from losing images when inferred color values (often brand/data specific)
    // don't match variation attribute values exactly.
    const fallbackImageGroups =
        !filteredImageGroups?.length && hasSelectableAttribute
            ? filterImageGroups(imageGroups, {
                  viewType: imageViewType,
                  variationValues: {}
              })
            : filteredImageGroups

    let matchedImageGroup

    // Try to match by color variation attribute
    if (effectiveColorValue && fallbackImageGroups?.length > 0) {
        matchedImageGroup = fallbackImageGroups.find((group) =>
            group.variationAttributes?.some(
                (attr) =>
                    attr.id === 'color' &&
                    attr.values?.some((val) => val.value === effectiveColorValue)
            )
        )
    }

    // Fallback to first filtered image group
    if (!matchedImageGroup && fallbackImageGroups?.length > 0) {
        matchedImageGroup = fallbackImageGroups[0]
    }

    const resolvedImages = matchedImageGroup?.images?.map((img) => ({...img})) || []

    // Final safety net: if enrichment imageGroups are present but unusable
    // (e.g., no matching/usable images), keep rendering the root product image
    // instead of returning an empty list that removes the image from DOM.
    if (!resolvedImages.length && product?.image) {
        return [
            {
                disBaseLink: product.image.disBaseLink,
                link: product.image.link,
                alt: product.image.alt,
                title: product.image.title
            },
            {}
        ]
    }

    return resolvedImages
}

const getProductPromotionIds = (productWithFilteredVariants) => {
    const variants =
        (productWithFilteredVariants.variants &&
            productWithFilteredVariants.variants.find((variant) => variant.productPromotions)) ||
        []

    let productPromotions
    if (variants?.length > 0) {
        productPromotions = variants.productPromotions
    } else {
        productPromotions = productWithFilteredVariants.productPromotions
    }

    return productPromotions?.map((promo) => promo.promotionId) || []
}

/**
 * Builds a mixed media sequence for the product tile carousel.
 * Inserts video items (from viewType:"video" imageGroups) at the positions
 * defined by c_videoSequencePositions (1-indexed string, e.g. "1,4").
 * When videoSequencePositions is absent, defaults to inserting the video at position 2.
 *
 * Returns an array of objects: {type:'image'|'video', src, alt}
 * Returns null when no video imageGroup is found for the current color.
 */
const buildTileMediaItems = ({imageUrls, imageGroups, colorValue, videoSequencePositions}) => {
    if (!imageGroups?.length || !imageUrls?.length) return null

    // Match the video group to the current color only (no cross-color fallback); a video
    // group without color metadata applies to all.
    const videoGroup = imageGroups.find((group) => {
        if (group.viewType !== 'video') return false
        const colorAttr = group.variationAttributes?.find((a) => a.id === 'color')
        if (!colorAttr?.values?.length) return true
        return colorAttr.values.some((v) => v.value === colorValue)
    })

    if (!videoGroup?.images?.length) return null

    const videoItems = videoGroup.images.map((img) => ({
        type: 'video',
        src: img.link,
        alt: img.alt || 'Product video'
    }))

    // Parse positions from attribute, or default to [1] (first slot) as fallback
    // when c_videoSequencePositions is not returned by the Search API.
    // Backend fix: mark c_videoSequencePositions as "returnable" in SFCC BM search index.
    const positions = videoSequencePositions
        ? String(videoSequencePositions)
              .split(',')
              .map((p) => parseInt(p.trim(), 10))
              .filter((p) => !isNaN(p) && p >= 1)
        : [1]

    if (!positions.length) return null

    const imageItems = imageUrls.map((src) => ({type: 'image', src, alt: ''}))

    // Each position[i] is the 1-indexed slot in the FINAL merged sequence for videoItems[i].
    // Build the result by placing videos at their absolute positions, then
    // filling remaining slots with images in their original order.
    const totalLength = imageItems.length + videoItems.length
    const result = new Array(totalLength)
    positions.forEach((pos, i) => {
        if (videoItems[i] && pos >= 1 && pos <= totalLength) {
            result[pos - 1] = videoItems[i]
        }
    })
    let imgIdx = 0
    for (let i = 0; i < totalLength; i++) {
        if (!result[i]) result[i] = imageItems[imgIdx++]
    }
    return result.filter(Boolean)
}

export {
    buildImageUrl,
    getPlpLcpImagePreloadAttrs,
    deriveNthImageUrl,
    getProductPromotionIds,
    getProductTileImages,
    buildTileMediaItems,
    stripQueryParams
}
